import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, ChevronUp, RefreshCw } from 'lucide-react'
import { usePlatformDailyReport } from '../../lib/usePlatformDailyReport'

function heroChipStatus(status) {
  switch (status) {
    case 'success':
      return 'success'
    case 'failed':
      return 'error'
    case 'partial':
      return 'warning'
    case 'error':
      return 'error'
    case 'none':
    default:
      return 'neutral'
  }
}

function heroChipLabel(status) {
  switch (status) {
    case 'success':
      return 'Passed'
    case 'failed':
      return 'Failed'
    case 'partial':
      return 'Partial'
    case 'error':
      return 'Error'
    case 'none':
      return 'No runs'
    default:
      return status || 'Unknown'
  }
}

function HeroStatusChip({ status }) {
  const variant = heroChipStatus(status)
  return (
    <span className={`overview-cron-chip overview-cron-chip--${variant}`}>
      {heroChipLabel(status)}
    </span>
  )
}

function formatEt(iso) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  } catch {
    return new Date(iso).toLocaleString()
  }
}

function SectionStatusDot({ status }) {
  const cls =
    status === 'ok'
      ? 'overview-daily-report-dot--ok'
      : status === 'warn'
        ? 'overview-daily-report-dot--warn'
        : status === 'error'
          ? 'overview-daily-report-dot--error'
          : 'overview-daily-report-dot--idle'
  return <span className={`overview-daily-report-dot ${cls}`} aria-hidden />
}

/**
 * Super-admin 24h platform report band — above AI Spotlight.
 */
export default function PlatformDailyReportBand() {
  const [expanded, setExpanded] = useState(false)
  const { loading, generating, error, displayReport, lastFailed, generate, refresh } =
    usePlatformDailyReport()

  const reportBody = displayReport?.report
  const hero = reportBody?.hero
  const stats = reportBody?.stats

  const groupedSections = useMemo(() => {
    const sections = Array.isArray(reportBody?.sections) ? reportBody.sections : []
    const map = new Map()
    for (const s of sections) {
      const g = s.group || 'Other'
      if (!map.has(g)) map.set(g, [])
      map.get(g).push(s)
    }
    return map
  }, [reportBody?.sections])

  const hasReport = Boolean(displayReport && reportBody?.hero)
  const showStaleWarning =
    Boolean(lastFailed?.error_message && !generating && displayReport && displayReport.id !== lastFailed.id)

  return (
    <section className="overview-band overview-daily-report overview-fade-in" aria-label="Last 24H Platform Report">
      <div className="overview-daily-report-header">
        <div>
          <h2 className="overview-daily-report-title">Last 24H Platform Report</h2>
          {displayReport?.generated_at ? (
            <p className="overview-daily-report-meta">
              Generated {formatEt(displayReport.generated_at)} ET
              {reportBody?.window_start && reportBody?.window_end ? (
                <>
                  {' · '}
                  Covers {formatEt(reportBody.window_start)} – {formatEt(reportBody.window_end)} ET
                </>
              ) : null}
            </p>
          ) : (
            <p className="overview-daily-report-meta">
              First scheduled run daily at 9:00 AM ET · or generate now
            </p>
          )}
        </div>
        <div className="overview-daily-report-actions">
          <button
            type="button"
            className="overview-btn-secondary"
            disabled={loading || generating}
            onClick={() => void generate()}
          >
            {generating ? (
              <>
                <RefreshCw size={14} className="overview-spin" aria-hidden />
                Generating…
              </>
            ) : (
              'Generate Report'
            )}
          </button>
          {hasReport ? (
            <button
              type="button"
              className="overview-btn-secondary"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? (
                <>
                  <ChevronUp size={14} aria-hidden />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown size={14} aria-hidden />
                  Expand
                </>
              )}
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="overview-daily-report-alert overview-daily-report-alert--error" role="alert">
          <span>{error}</span>
          <button type="button" className="overview-btn-secondary" onClick={() => void refresh()}>
            Retry
          </button>
        </div>
      ) : null}

      {showStaleWarning && lastFailed?.error_message ? (
        <div className="overview-daily-report-alert overview-daily-report-alert--warn" role="status">
          Report generation failed: {lastFailed.error_message}
          {displayReport ? ' · Showing last successful report' : null}
        </div>
      ) : null}

      {generating ? (
        <div className="overview-daily-report-progress" role="status">
          <div className="overview-daily-report-progress-bar" />
          <span>Aggregating 24h activity across platform modules…</span>
        </div>
      ) : null}

      {loading && !hasReport ? (
        <div className="overview-daily-report-hero overview-daily-report-hero--skeleton">
          <div className="overview-skeleton" style={{ height: 20, width: '60%' }} />
        </div>
      ) : null}

      {!loading && !hasReport && !generating ? (
        <div className="overview-daily-report-empty">
          <p>No report yet. Click Generate Report to build the first 24h snapshot.</p>
        </div>
      ) : null}

      {hero ? (
        <div
          className={`overview-daily-report-hero${generating ? ' overview-daily-report-hero--dimmed' : ''}`}
        >
          <span className="overview-daily-report-hero-label">Basecamp Sync</span>
          <HeroStatusChip status={hero.basecamp_sync_status} />
          <span className="overview-daily-report-hero-summary">{hero.basecamp_sync_summary}</span>
          {!expanded && hasReport ? (
            <span className="overview-daily-report-hero-hint overview-muted-copy">
              Tap Expand for full platform breakdown
            </span>
          ) : null}
        </div>
      ) : null}

      {expanded && hasReport ? (
        <div className="overview-daily-report-expanded">
          {[...groupedSections.entries()].map(([group, items]) => (
            <div key={group} className="overview-daily-report-group">
              <h3 className="overview-daily-report-group-title">{group}</h3>
              <ul className="overview-daily-report-list">
                {items.map((section) => (
                  <li key={section.id} className="overview-daily-report-row">
                    <SectionStatusDot status={section.status} />
                    <span className="overview-daily-report-row-label">{section.label}</span>
                    <span
                      className={`overview-daily-report-row-headline${
                        section.status === 'idle' ? ' overview-muted-copy' : ''
                      }`}
                    >
                      {section.headline}
                    </span>
                    <Link to={section.href} className="overview-daily-report-row-link">
                      Open
                      <ChevronRight size={14} aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {stats ? (
            <p className="overview-daily-report-footer overview-muted-copy">
              {stats.activeCount} module{stats.activeCount === 1 ? '' : 's'} with activity ·{' '}
              {stats.idleCount} quiet
              {displayReport?.generated_at ? ` · Generated ${formatEt(displayReport.generated_at)} ET` : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
